import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Sparkles, Calendar, Star, CheckCircle2, AlertTriangle, FileText, Megaphone } from "lucide-react";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";
import { DeleteAccountCard } from "@/components/DeleteAccountCard";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import { MediaManager } from "@/components/MediaManager";
import { AvailabilityManager } from "@/components/AvailabilityManager";
import { BookingCard } from "@/components/BookingCard";
import { PlanCard, BioCard, StatsCard, MyReviewsCard } from "@/components/ProviderPlan";
import { SocialLinksCard } from "@/components/SocialLinks";
import { TaxonomyCard } from "@/components/TaxonomyCard";
import { DateField, todayRome } from "@/components/DateField";
import { ComplianceCard } from "@/components/ComplianceCard";
import { PaBillingCard } from "@/components/PaBillingCard";
import { MepaRequestsCard } from "@/components/MepaRequestsCard";
import { SponsorCard } from "@/components/SponsorCard";
import { CoverageTravelCard } from "@/components/CoverageTravelCard";
import { PackageSettingsCard } from "@/components/PackageSettings";
import { KitProfileCard } from "@/components/KitProfileForm";
import { EventsManager } from "@/components/EventsManager";
import { PaProfileCard } from "@/components/PaProfileCard";
import { InvoicesCard } from "@/components/InvoicesCard";
import { KeyRound } from "lucide-react";
import { HelpButton, SuggestionsCard } from "@/components/HelpCenter";
import { SiaeCard } from "@/components/SiaeCard";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [ads, setAds] = useState([]);
  const [adsLoaded, setAdsLoaded] = useState(false);
  const [brokerForm, setBrokerForm] = useState({ eventDate:"", eventLocation:"", eventType:"", notes:"" });

  const [newIds, setNewIds] = useState([]);
  const loadBookings = async () => {
    const r = await api.get("/bookings");
    setBookings(r.data);
    const fresh = r.data.filter(b=>b.clientSeen===false && b.clientId===user.id).map(b=>b.id);
    if (fresh.length) {
      setNewIds(p=>[...new Set([...p, ...fresh])]);
      await api.post("/bookings/mark-seen");
    }
    window.dispatchEvent(new Event("bookings-changed"));
  };
  useEffect(()=>{
    if (!user) return;
    loadBookings();
    if (user.role === "admin") { api.get("/ads", { params: { all: true } }).then(r=>{ setAds(r.data); setAdsLoaded(true); }); }
  }, [user]);

  if (!user) return <div className="max-w-3xl mx-auto px-6 py-16 text-center"><p>Devi accedere.</p><Link to="/login" className="btn-pill-gold inline-flex mt-4">Accedi</Link></div>;

  const isProvider = ["artist","supplier"].includes(user.role);
  const pendingCount = bookings.filter(b=>b.status==="pending_artist").length;
  const outcomes = bookings.filter(b=>newIds.includes(b.id));
  const OUT = { confirmed: "controfirmato", rejected: "rifiutato", expired: "scaduto senza risposta" };

  const activateRecommended = async () => {
    try {
      await api.post("/me/recommended/activate");
      toast.success("Consigliati attivato · €12/anno");
      refresh();
    } catch(err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const approveAd = async (adId) => {
    await api.post(`/ads/${adId}/approve`);
    toast.success("Inserzione approvata");
    const r = await api.get("/ads", { params: { all: true } }); setAds(r.data);
  };

  const sendBroker = async (e) => {
    e.preventDefault();
    if (!brokerForm.eventDate) { toast.error("Scegli la data dell'evento"); return; }
    try {
      await api.post("/broker-requests", brokerForm);
      toast.success("Richiesta inviata al portale, sarai ricontattato.");
      setBrokerForm({ eventDate:"", eventLocation:"", eventType:"", notes:"" });
    } catch(err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <span className="overline text-gold">Dashboard · {{ artist: "Artista", supplier: "Fornitore", public_entity: "Ente Pubblico", business: "Impresa / P.IVA", citizen: "Privato", admin: "Amministratore" }[user.role] || user.role}</span>
          <h1 className="font-display text-3xl font-bold mt-2">Ciao, {user.firstName || user.companyName || user.entityName || user.email}</h1>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {user.role !== "admin" && <HelpButton />}
          {user.role==="public_entity" && <span className="chip-gold">Account GRATUITO</span>}
          {["artist","supplier","business"].includes(user.role) && user.subscription?.status === "trialing" && <span className="chip-gold">Prova gratuita attiva</span>}
          {user.isRecommendedEvent && <span className="chip-gold">Nei Consigliati</span>}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Stats */}
        <StatCard icon={Calendar} label="Prenotazioni" value={bookings.length} />
        {["artist","supplier"].includes(user.role) && <StatCard icon={Star} label="Media voto" value={(user.rating||0).toFixed(1)} />}
        {["artist","supplier"].includes(user.role) && <StatCard icon={CheckCircle2} label="Recensioni" value={user.reviewCount||0} />}
        {user.role==="citizen" && <StatCard icon={Sparkles} label="Richieste broker" value={bookings.filter(b=>b.status==="broker_request").length} />}
      </div>

      {user.role==="admin" && (
        <div className="mt-8 card-premium p-6" data-testid="admin-ads-panel">
          <div className="overline text-gold mb-4">Moderazione ADV</div>
          {!adsLoaded && <p className="text-sm text-slate-500">Caricamento…</p>}
          {adsLoaded && ads.filter(a=>a.status==="pending").length===0 && <p className="text-sm text-slate-500">Nessuna inserzione in attesa.</p>}
          <div className="space-y-3">
            {ads.filter(a=>a.status==="pending").map(a=>(
              <div key={a.id} className="flex items-center justify-between gap-4 border border-slate-200 rounded-xl p-4">
                <div className="min-w-0">
                  <div className="font-semibold">{a.advertiser}</div>
                  <div className="text-xs text-slate-500 truncate">{a.offerText} · {a.days}gg · €{a.totalGross.toFixed(2)}</div>
                </div>
                <button data-testid={`approve-ad-${a.id}`} onClick={()=>approveAd(a.id)} className="btn-pill-gold text-sm">Approva</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {user.role === "public_entity" && <PaProfileCard />}
      {["public_entity","business","citizen"].includes(user.role) && (
        <div className="mt-8 card-premium p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="dashboard-turnkey">
          <KeyRound className="w-8 h-8 text-gold shrink-0"/>
          <div className="flex-1"><h3 className="font-display text-xl font-bold">Evento Chiavi in Mano</h3><p className="text-sm text-slate-600">Rispondi a un breve questionario guidato: ti proponiamo pacchetti completi di artisti e fornitori disponibili.</p></div>
          <div className="flex gap-2"><Link to="/chiavi-in-mano" data-testid="dashboard-turnkey-new" className="btn-pill-gold text-sm">Evento Chiavi in Mano</Link><Link to="/pacchetti" data-testid="dashboard-turnkey-mine" className="btn-pill-outline text-sm">I miei pacchetti</Link></div>
        </div>
      )}
      {["artist","supplier"].includes(user.role) && <CoverageTravelCard />}
      {["artist","supplier"].includes(user.role) && <PackageSettingsCard />}
      {["artist","supplier"].includes(user.role) && <KitProfileCard />}
      {["artist","supplier"].includes(user.role) && <div className="mt-4"><Link to="/pacchetti" data-testid="dashboard-package-requests" className="btn-pill-outline text-sm"><KeyRound className="w-4 h-4"/>Richieste pacchetti Evento Chiavi in Mano</Link></div>}
      {["artist","supplier"].includes(user.role) && <SponsorCard />}

      {/* Recommended activation for artists/suppliers */}
      {["artist","supplier"].includes(user.role) && (
        <div className="mt-8 card-premium p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2"><Sparkles className="w-5 h-5 text-gold"/><h3 className="font-display text-xl font-bold">Consigliati</h3></div>
              <p className="text-sm text-slate-600 max-w-xl">Add-on 12,00€/anno. Richiede media voto ≥ 3,5. Il portale ti promuove direttamente con mandato di vendita.</p>
              {user.recommendedStatus==="grace" && (
                <div className="mt-3 flex items-center gap-2 text-amber-700 text-sm"><AlertTriangle className="w-4 h-4"/>Grace period: hai 90 giorni per riportare la media sopra 3,5 stelle.</div>
              )}
            </div>
            {!user.isRecommendedEvent && <button data-testid="activate-recommended" onClick={activateRecommended} className="btn-pill-gold">Attiva Consigliati (€12/anno)</button>}
            {user.isRecommendedEvent && <span className="chip-gold">Attiva · {user.recommendedStatus}</span>}
          </div>
        </div>
      )}

      {/* Citizen broker form */}
      {user.role==="citizen" && (
        <div className="mt-8 card-premium p-6">
          <div className="overline text-gold mb-2">Richiesta evento (Flusso Broker)</div>
          <h3 className="font-display text-xl font-bold mb-3">Non vedi i prezzi? Pensiamo a tutto noi.</h3>
          <p className="text-sm text-slate-600 mb-5">Compila la richiesta, il Portale ti ricontatterà con un preventivo chiaro e gestirà l'artista per te.</p>
          <form onSubmit={sendBroker} className="grid md:grid-cols-2 gap-4">
            <DateField testId="broker-date" value={brokerForm.eventDate} min={todayRome()} placeholder="Data evento (gg/mm/aaaa)" onChange={(v)=>setBrokerForm({...brokerForm, eventDate:v})} className="border border-slate-300 rounded-xl px-4 py-2.5 text-sm"/>
            <input required placeholder="Luogo evento" value={brokerForm.eventLocation} onChange={(e)=>setBrokerForm({...brokerForm, eventLocation:e.target.value})} className="border border-slate-300 rounded-xl px-4 py-2.5 text-sm" data-testid="broker-location"/>
            <input required placeholder="Tipo spettacolo (es. compleanno, matrimonio)" value={brokerForm.eventType} onChange={(e)=>setBrokerForm({...brokerForm, eventType:e.target.value})} className="border border-slate-300 rounded-xl px-4 py-2.5 text-sm md:col-span-2" data-testid="broker-type"/>
            <textarea placeholder="Note aggiuntive" value={brokerForm.notes} onChange={(e)=>setBrokerForm({...brokerForm, notes:e.target.value})} className="border border-slate-300 rounded-xl px-4 py-2.5 text-sm md:col-span-2" rows={3}/>
            <button data-testid="broker-submit" className="btn-pill-gold md:col-span-2">Invia richiesta al Portale</button>
          </form>
        </div>
      )}

      {/* Bookings */}
      <div className="mt-10">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="overline text-gold flex items-center gap-2"><FileText className="w-4 h-4"/>Le tue prenotazioni e contratti</div>
          <Link to="/contratti" data-testid="dashboard-contract-archive" className="btn-pill-outline text-xs"><FileText className="w-3.5 h-3.5"/>Archivio contratti</Link>
        </div>
        {outcomes.length>0 && (
          <div className="mb-4 rounded-xl bg-gold/15 border border-gold px-4 py-3 text-sm text-navy space-y-1" data-testid="outcome-alert">
            <div className="font-semibold">Novità sui tuoi contratti</div>
            {outcomes.map(b=><div key={b.id} data-testid={`outcome-${b.id}`}>{b.artistName} · {b.eventDate}: <b>{OUT[b.status] || b.status}</b></div>)}
          </div>
        )}
        {isProvider && pendingCount>0 && (
          <div className="mb-4 rounded-xl bg-gold/15 border border-gold px-4 py-3 text-sm text-navy font-semibold" data-testid="pending-contracts-alert">
            Hai {pendingCount} {pendingCount===1?"contratto":"contratti"} da controfirmare: hai 24 ore per accettare o rifiutare l'impegno.
          </div>
        )}
        {bookings.length===0 && <div className="card-premium p-8 text-center text-slate-500 text-sm">Nessuna prenotazione.</div>}
        <div className="grid md:grid-cols-2 gap-4">
          {[...bookings].sort((x,y)=>(y.status==="pending_artist")-(x.status==="pending_artist")).map((b)=>(
            <BookingCard key={b.id} b={b} isProvider={isProvider} isNew={newIds.includes(b.id)} onChanged={loadBookings} />
          ))}
        </div>
      </div>
      {["artist","supplier","public_entity","business"].includes(user.role) && <MepaRequestsCard />}
      {["artist","supplier"].includes(user.role) && <PlanCard />}
      {["artist","supplier"].includes(user.role) && <StatsCard />}
      <SubscriptionCard />
      {["artist","supplier"].includes(user.role) && <TaxonomyCard />}
      {["artist","supplier"].includes(user.role) && <BioCard />}
      {["artist","supplier"].includes(user.role) && <SiaeCard />}
      {["artist","supplier"].includes(user.role) && <SocialLinksCard />}
      {["artist","supplier"].includes(user.role) && <AvailabilityManager />}
      {["artist","supplier"].includes(user.role) && <MediaManager />}
      {["artist","supplier"].includes(user.role) && <ComplianceCard />}
      {["public_entity","business"].includes(user.role) && <PaBillingCard />}
      {["artist","supplier"].includes(user.role) && <MyReviewsCard />}
      {["public_entity","artist","supplier"].includes(user.role) && <EventsManager />}
      <InvoicesCard />
      <ChangePasswordCard />
      {user.role !== "admin" && <DeleteAccountCard />}
      {user.role !== "admin" && <SuggestionsCard />}
    </div>
  );
}

function StatCard({ icon:Icon, label, value }) {
  return (
    <div className="card-premium p-6">
      <Icon className="w-6 h-6 text-gold mb-3"/>
      <div className="overline text-slate-500">{label}</div>
      <div className="font-display text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}
